import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import "./Chat.css";
import { ChatLoader } from "./ChatLoader";

const Chat = ({ text, image, timestamp, setOpen, modelConfig }) => {
  const [messages, setMessages] = useState(
    JSON.parse(sessionStorage.getItem("chatMessages")) || []
  );
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const endOfMessagesRef = useRef(null);

  let BASE_URL =
    process.env.REACT_APP_API_HOST_URL?.trim() || "https://devapi.fiduciaai.in";
  const sessionId = sessionStorage.getItem("sessionId") || "default_session_id";

  const storeMessages = (messages) => {
    const last5Messages = messages.slice(-5);
    sessionStorage.setItem("chatMessages", JSON.stringify(last5Messages));
  };

  // Scroll to the bottom on new messages
  useLayoutEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle new incoming props (text/image)
  useEffect(() => {
    if (text || image) {
      const newMessage = {
        question: { text: text || "", image: image || null },
        response: "",
        isStreaming: true,
        showMore: true,
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      fetchResponse(newMessage);
    }
  }, [text, image, timestamp]);

  const fetchResponse = async (newMessage) => {
    setIsLoading(true);
    setError(null);

    try {
      let response;
      if (image) {
        const formData = new FormData();
        formData.append("customerkey", modelConfig.customerKey);
        formData.append("ocid", modelConfig.ocid);
        formData.append("query", newMessage.question.text || "");
        formData.append(
          "default_query",
          modelConfig.defaultPrompt || "Some default query"
        );
        formData.append("html_flag", "true");
        formData.append("model", modelConfig.model);
        formData.append("threshold", modelConfig.threshold);
        formData.append("context_prompt", modelConfig.context_prompt);
        formData.append("record_size", modelConfig.record_size);
        formData.append("conversation_id", sessionId);

        // Append the image only if it exists
        if (newMessage.question.image) {
          let blob = null;
          if (
            typeof newMessage.question.image === "string" &&
            newMessage.question.image.startsWith("data:image/")
          ) {
            const response = await fetch(newMessage.question.image);
            blob = await response.blob();
          } else {
            blob = newMessage.question.image;
          }
          const file = new File([blob], "query.png", {
            type: blob.type || "image/png",
          });
          formData.append("images", file);
        }

        response = await fetch(
          BASE_URL + "/rag/api/v2/chat/img/stream/response",
          {
            method: "POST",
            body: formData,
            headers: { Accept: "text/event-stream" },
          }
        );
      } else {
        const payload = {
          query: newMessage.question.text || "",
          conversation_id: sessionId || "default_session_id",
          default_query: modelConfig.defaultPrompt || "Some default prompt",
          customerkey: modelConfig.customerKey,
          ocid: modelConfig.ocid,
          html_flag: false,
          model: modelConfig.model,
          threshold: modelConfig.threshold,
          context_prompt: modelConfig.context_prompt,
          record_size: modelConfig.record_size || 100,
        };

        response = await fetch(BASE_URL + "/rag/api/v2/chat/stream/response", {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
          },
        });
      }

      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let currentIndex = messages.length;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const extractedContent = (chunk.match(/\$(.*?)\$/gs) || [])
            .map((str) => str.slice(1, -1))
            .join("");

          if (extractedContent.includes("duration_for_embeddings")) continue;

          setMessages((prevMessages) =>
            prevMessages.map((msg, index) =>
              index === currentIndex
                ? {
                    ...msg,
                    response: (msg.response || "") + extractedContent,
                    isStreaming: true,
                  }
                : msg
            )
          );
        }

        setMessages((prevMessages) =>
          prevMessages.map((msg, index) =>
            index === currentIndex ? { ...msg, isStreaming: false } : msg
          )
        );
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching response:", error);
      setError("Something went wrong while querying.");
      setIsLoading(false);
    }
  };

  const handleTryAgain = () => {
    setError(null);
    setOpen(false);
  };

  return (
    <div className="chat-container">
      {messages.map((msg, index) => (
        <div key={index} className="message">
          <div className="question-bubble">
            {msg.question.image && (
              <img src={msg.question.image} alt="Prompt" />
            )}
            {msg.question.text && <p>{msg.question.text}</p>}
          </div>

          {msg.response && (
            <div className="response-bubble">
              <p dangerouslySetInnerHTML={{ __html: msg.response }} />
            </div>
          )}

          {msg.isStreaming && <ChatLoader />}
        </div>
      ))}

      {/* Empty State */}
      {messages.length === 0 && !isLoading && (
        <div className="empty-message">
          <p>Try typing a message or uploading an image to start the chat!</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-message">
          <p>
            {error}{" "}
            <span
              onClick={handleTryAgain}
              style={{ color: "blue", cursor: "pointer" }}
            >
              Try Again!
            </span>
          </p>
        </div>
      )}

      <div ref={endOfMessagesRef} />
    </div>
  );
};

export default Chat;
